import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  AreaChart,
  Area,
} from 'recharts';
import { LeadVelocityPoint } from '@/services/pilot-crm-analytics.service';

export interface ConversionChartProps {
  data: LeadVelocityPoint[];
  chartType?: 'line' | 'area';
}

export const ConversionChart: React.FC<ConversionChartProps> = ({
  data,
  chartType = 'area',
}) => {
  const formattedData = data.map((point) => ({
    ...point,
    date: new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  }));

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-500">No data available for the selected period</p>
      </div>
    );
  }

  const Chart = chartType === 'area' ? AreaChart : LineChart;

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <Chart
          data={formattedData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            interval="preserveStartEnd"
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
            }}
          />
          <Legend />
          {chartType === 'area' ? (
            <>
              <Area
                type="monotone"
                dataKey="newLeads"
                name="New Leads"
                stroke="#3B82F6"
                fill="#93C5FD"
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey="totalActive"
                name="Active Leads"
                stroke="#8B5CF6"
                fill="#C4B5FD"
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey="liveLeads"
                name="Live"
                stroke="#22C55E"
                fill="#86EFAC"
                fillOpacity={0.6}
              />
            </>
          ) : (
            <>
              <Line
                type="monotone"
                dataKey="newLeads"
                name="New Leads"
                stroke="#3B82F6"
                strokeWidth={2}
                dot={{ fill: '#3B82F6', r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="totalActive"
                name="Active Leads"
                stroke="#8B5CF6"
                strokeWidth={2}
                dot={{ fill: '#8B5CF6', r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="liveLeads"
                name="Live"
                stroke="#22C55E"
                strokeWidth={2}
                dot={{ fill: '#22C55E', r: 4 }}
              />
            </>
          )}
        </Chart>
      </ResponsiveContainer>
    </div>
  );
};

export interface SourceBreakdownChartProps {
  data: Record<string, number>;
}

const SOURCE_COLORS: Record<string, string> = {
  pilot_page: '#3B82F6',
  cold_email: '#8B5CF6',
  referral: '#22C55E',
  inbound: '#F59E0B',
  partner: '#EC4899',
  google_places: '#06B6D4',
  other: '#6B7280',
};

const SOURCE_LABELS: Record<string, string> = {
  pilot_page: 'Pilot Page',
  cold_email: 'Cold Email',
  referral: 'Referral',
  inbound: 'Inbound',
  partner: 'Partner',
  google_places: 'Google Places',
  other: 'Other',
};

export const SourceBreakdownChart: React.FC<SourceBreakdownChartProps> = ({ data }) => {
  const total = Object.values(data).reduce((sum, val) => sum + val, 0);

  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-48 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-500">No source data available</p>
      </div>
    );
  }

  const sortedEntries = Object.entries(data)
    .filter(([, value]) => value > 0)
    .sort(([, a], [, b]) => b - a);

  return (
    <div className="space-y-3">
      {sortedEntries.map(([source, count]) => {
        const percentage = (count / total) * 100;
        return (
          <div key={source}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-700">{SOURCE_LABELS[source] || source}</span>
              <span className="text-gray-500">
                {count} ({percentage.toFixed(1)}%)
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div
                className="h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${percentage}%`,
                  backgroundColor: SOURCE_COLORS[source] || '#6B7280',
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
